import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Sparkles, Loader2, User, KeyRound, Award, Lock, Check, ArrowRight, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { StepBasics, StepPin, StepLicense, StepPayroll } from "@/components/OnboardingSteps";

const STEPS = [
  { key: "basics", label: "Basics", icon: User, render: StepBasics },
  { key: "pin", label: "Clock-in PIN", icon: KeyRound, render: StepPin },
  { key: "license", label: "License", icon: Award, render: StepLicense },
  { key: "payroll", label: "Payroll (encrypted)", icon: Lock, render: StepPayroll },
];

const blank = () => ({
  name: "", title: "", employee_role: "", department: "", email: "", phone: "",
  hire_date: "", address: "", allergies: "",
  emergency_contact_name: "", emergency_contact_phone: "", emergency_contact_relationship: "",
  pin: "", pin_confirm: "",
  license_number: "", license_expiry: "", issuing_state: "",
  hourly_rate: "", initial_pto_hours: 40,
  ssn: "", bank_account: "", routing_number: "", dob: "",
});

const StepIndicator = ({ step }) => (
  <div className="flex items-center justify-between mb-5 mt-1">
    {STEPS.map((s, i) => {
      const Icon = s.icon;
      const done = i < step;
      const active = i === step;
      return (
        <div key={s.key} className="flex-1 flex items-center" data-testid={`onboard-step-${s.key}`}>
          <div className="flex flex-col items-center gap-1.5 flex-shrink-0 w-16 sm:w-20">
            <div className={`w-9 h-9 rounded-full flex items-center justify-center border-2 transition-colors ${
              done ? "bg-pink-700 border-pink-700 text-white" :
              active ? "bg-pink-50 border-pink-700 text-pink-800" :
              "bg-slate-50 border-slate-200 text-slate-400"
            }`}>
              {done ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
            </div>
            <span className={`text-[10px] sm:text-xs text-center leading-tight ${active ? "font-semibold text-pink-800" : "text-slate-500"}`}>{s.label}</span>
          </div>
          {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 ${i < step ? "bg-pink-700" : "bg-slate-200"} -mt-4`} />}
        </div>
      );
    })}
  </div>
);

const validate = (step, form) => {
  if (step === 0) {
    if (!form.name.trim()) return "Full name is required";
    if (!form.employee_role) return "Role is required";
  }
  if (step === 1 && form.pin) {
    if (!/^\d{4,8}$/.test(form.pin)) return "PIN must be 4-8 digits";
    if (form.pin !== form.pin_confirm) return "PINs do not match";
  }
  if (step === 2 && form.license_expiry) {
    const t = new Date(); t.setHours(0, 0, 0, 0);
    if (new Date(form.license_expiry) < t) return "License expiry cannot be in the past";
  }
  return null;
};

export default function OnboardingWizard({ open, onClose, onCreated }) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(blank());
  const [saving, setSaving] = useState(false);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const reset = () => { setForm(blank()); setStep(0); setSaving(false); };
  const close = () => { reset(); onClose(); };

  const next = () => {
    const err = validate(step, form);
    if (err) return toast.error(err);
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
  };
  const back = () => setStep((s) => Math.max(0, s - 1));

  const submit = async () => {
    const err = validate(step, form);
    if (err) return toast.error(err);
    setSaving(true);
    try {
      const payload = { ...form };
      delete payload.pin_confirm;
      const { data } = await api.post("/employees/onboard", payload);
      const bits = ["employee created"];
      if (form.pin) bits.push("PIN set");
      if (data.license_id) bits.push("license tracked");
      if ((data.payroll_fields || []).length) bits.push(`${data.payroll_fields.length} payroll field(s) encrypted`);
      toast.success(bits.join(" · "));
      onCreated?.();
      close();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Onboarding failed");
    } finally { setSaving(false); }
  };

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const StepBody = STEPS[step].render;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="sm:max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="onboarding-wizard">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-pink-700" /> Employee Onboarding Wizard
          </DialogTitle>
          <p className="text-xs text-slate-500">
            Add a new team member — basics, clock-in PIN, license, and encrypted payroll in one guided flow.
          </p>
        </DialogHeader>

        <StepIndicator step={step} />
        <StepBody form={form} upd={upd} today={today} />

        <DialogFooter className="flex justify-between gap-2 mt-4">
          <Button variant="outline" onClick={back} disabled={step === 0 || saving} className="gap-1.5"
                  data-testid="onboard-back">
            <ArrowLeft className="w-4 h-4" /> Back
          </Button>
          {step < STEPS.length - 1 ? (
            <Button onClick={next} className="bg-pink-700 hover:bg-pink-800 gap-1.5" data-testid="onboard-next">
              Next <ArrowRight className="w-4 h-4" />
            </Button>
          ) : (
            <Button data-testid="onboard-finish" onClick={submit} disabled={saving}
                    className="bg-pink-700 hover:bg-pink-800 gap-1.5">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Finish & Create
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
